"use client";

// Meta Pixel loader. Browser-side only.
//
// No-ops entirely when NEXT_PUBLIC_META_PIXEL_ID is unset or the visitor hasn't accepted
// cookies — matching the graceful-no-op convention every other integration in .env.example
// follows (PostHog, Turnstile, proxy secret, Basic Auth), so local dev and CI need no config.
//
// PageView is fired once by the base snippet on the initial load, then again on every
// client-side route change (this is a Next.js App Router SPA — there is no full reload to
// trigger Meta's own pageview tracking).

import Script from "next/script";
import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { useConsent } from "@/lib/consent";

const PIXEL_ID = process.env.NEXT_PUBLIC_META_PIXEL_ID;

export function MetaPixel() {
  const consent = useConsent();
  const pathname = usePathname();

  // The pathname the base snippet's own `fbq('track', 'PageView')` already covered.
  // Seeded from the effect below on its first run, NOT from the Script's onLoad — an
  // inline <script> (this one has no `src`, just `children`) never fires a DOM `load`
  // event, so next/script's onLoad is never called for it (see next/dist/client/script.js:
  // the `children` branch runs afterLoad() → onReady only, never onLoad). Relying on
  // onLoad here previously meant this ref never left `null` and SPA PageViews never fired.
  const trackedPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!PIXEL_ID || consent !== "accepted") return;
    if (trackedPathnameRef.current === null) {
      // First render where the Pixel is active — the base snippet below already fired
      // PageView for this pathname, so just record it without sending a duplicate.
      trackedPathnameRef.current = pathname;
      return;
    }
    if (pathname === trackedPathnameRef.current) return;
    trackedPathnameRef.current = pathname;
    window.fbq?.("track", "PageView");
  }, [pathname, consent]);

  if (!PIXEL_ID || consent !== "accepted") return null;

  return (
    <Script id="meta-pixel" strategy="afterInteractive">
      {`
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${PIXEL_ID}');
        fbq('track', 'PageView');
      `}
    </Script>
  );
}
